// src/components/workAccountability/TaskTimeEntries.tsx
//
// Part 6: task time entries — Add Time, Void, Correction (never in-place edits),
// and the full history including correction chains. Correction Reason is shown
// only when the resolved policy requires it (editReasonRequired). Backend
// validation is authoritative — we surface its errors, never duplicate them.

import { useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  useGetTaskTimeQuery,
  useAddTaskTimeMutation,
  useCorrectTimeEntryMutation,
  useVoidTimeEntryMutation,
} from '../../api/accountabilityApi';
import type { TaskTimeEntry } from '../../types/workAccountability';
import { formatMinutes } from './waFormat';
import { Banner } from './waControls';

interface Props {
  taskId: string;
  editReasonRequired?: boolean;
}

function todayIso(): string {
  // Local calendar date (not UTC) so evening users in negative offsets don't
  // default the work date to tomorrow.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function backendError(e: unknown): string {
  return (e as { data?: { error?: string } })?.data?.error ?? 'Something went wrong.';
}

const STATUS_BADGE: Record<TaskTimeEntry['status'], string> = {
  active: 'bg-green-100 text-green-700',
  superseded: 'bg-gray-100 text-gray-500',
  voided: 'bg-red-100 text-red-600',
};

export default function TaskTimeEntries({ taskId, editReasonRequired }: Props) {
  const { data, isLoading, isError, refetch } = useGetTaskTimeQuery(taskId, { skip: !taskId });
  const [addTime, { isLoading: adding }] = useAddTaskTimeMutation();
  const [correct, { isLoading: isCorrecting }] = useCorrectTimeEntryMutation();
  const [voidEntry] = useVoidTimeEntryMutation();

  const [minutes, setMinutes] = useState('');
  const [workDate, setWorkDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const [correcting, setCorrecting] = useState<TaskTimeEntry | null>(null);
  const [cMinutes, setCMinutes] = useState('');
  const [cReason, setCReason] = useState('');
  const [confirmVoid, setConfirmVoid] = useState<TaskTimeEntry | null>(null);

  const submitAdd = async () => {
    setBanner(null);
    const m = Math.floor(Number(minutes));
    if (!Number.isFinite(m) || m <= 0) {
      setBanner({ kind: 'error', message: 'Enter a positive number of minutes.' });
      return;
    }
    try {
      await addTime({ taskId, minutes: m, workDate, note: note.trim() || null }).unwrap();
      setMinutes('');
      setNote('');
      setBanner({ kind: 'success', message: 'Time added.' });
    } catch (e) {
      setBanner({ kind: 'error', message: backendError(e) });
    }
  };

  const openCorrect = (entry: TaskTimeEntry) => {
    setCorrecting(entry);
    setCMinutes(String(entry.minutes));
    setCReason('');
    setBanner(null);
  };
  const submitCorrect = async () => {
    if (!correcting) return;
    setBanner(null);
    const m = Math.floor(Number(cMinutes));
    if (!Number.isFinite(m) || m <= 0) {
      setBanner({ kind: 'error', message: 'Enter a positive number of minutes.' });
      return;
    }
    try {
      await correct({ id: correcting.id, taskId, expectedVersion: correcting.version, minutes: m, correctionReason: cReason.trim() || null }).unwrap();
      setCorrecting(null);
      setBanner({ kind: 'success', message: 'Correction applied.' });
    } catch (e) {
      // Re-read the list so a stale version (409) is refreshed rather than looping.
      refetch();
      setCorrecting(null);
      setBanner({ kind: 'error', message: backendError(e) });
    }
  };

  const doVoid = async () => {
    const entry = confirmVoid;
    if (!entry) return;
    try {
      await voidEntry({ id: entry.id, taskId, expectedVersion: entry.version }).unwrap();
      setBanner({ kind: 'success', message: 'Entry voided.' });
    } catch (e) {
      refetch(); // refresh a stale version (409) instead of looping
      setBanner({ kind: 'error', message: backendError(e) });
    } finally {
      setConfirmVoid(null);
    }
  };

  return (
    <div data-testid="wa-time-entries">
      {banner && <div className="mb-2"><Banner kind={banner.kind} message={banner.message} /></div>}

      {/* Add time */}
      <div className="rounded-lg border border-gray-200 p-3 mb-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Add Time</div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="wa-add-min" className="block text-xs text-gray-600 mb-0.5">Minutes</label>
            <input id="wa-add-min" type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label htmlFor="wa-add-date" className="block text-xs text-gray-600 mb-0.5">Date</label>
            <input id="wa-add-date" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex-1 min-w-[8rem]">
            <label htmlFor="wa-add-note" className="block text-xs text-gray-600 mb-0.5">Note</label>
            <input id="wa-add-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <Button size="sm" variant="primary" onClick={submitAdd} isLoading={adding}>Add</Button>
        </div>
      </div>

      {/* History */}
      {isLoading ? (
        <div className="h-16 w-full animate-pulse rounded-lg bg-gray-100" />
      ) : isError ? (
        <p className="text-sm text-gray-500">Could not load time entries.</p>
      ) : !data || data.entries.length === 0 ? (
        <p className="text-sm text-gray-500">No time logged yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {data.entries.map((entry) => (
            <li key={entry.id} className={`p-3 ${entry.supersedesEntryId ? 'pl-6' : ''}`} data-testid={`wa-time-entry-${entry.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${entry.status === 'active' ? 'text-gray-900' : 'text-gray-500 line-through'}`}>{formatMinutes(entry.minutes)}</span>
                    <span className="text-gray-500">{entry.workDate}</span>
                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[entry.status]}`}>{entry.status}</span>
                    {entry.supersedesEntryId && <span className="text-[10px] text-gray-400">correction</span>}
                  </div>
                  {entry.note && <div className="text-xs text-gray-500">{entry.note}</div>}
                  {entry.correctionReason && <div className="text-xs text-gray-400">Reason: {entry.correctionReason}</div>}
                </div>
                {entry.status === 'active' && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openCorrect(entry)}>Correct</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmVoid(entry)}>Void</Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={!!correcting}
        onClose={() => setCorrecting(null)}
        title="Correct entry"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCorrecting(null)} disabled={isCorrecting}>Cancel</Button>
            <Button variant="primary" onClick={submitCorrect} isLoading={isCorrecting}>Apply correction</Button>
          </div>
        }
      >
        <p className="text-xs text-gray-500 mb-3">A correction preserves the original entry and creates a replacement. History is never edited in place.</p>
        <div className="space-y-3">
          <div>
            <label htmlFor="wa-cor-min" className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
            <input id="wa-cor-min" type="number" min={1} value={cMinutes} onChange={(e) => setCMinutes(e.target.value)} className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          {editReasonRequired && (
            <div>
              <label htmlFor="wa-cor-reason" className="block text-sm font-medium text-gray-700 mb-1">Correction Reason <span className="text-red-500">*</span></label>
              <input id="wa-cor-reason" value={cReason} onChange={(e) => setCReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmVoid}
        title="Void entry?"
        message="This time entry will be excluded from totals. It stays in the history."
        confirmLabel="Void"
        variant="danger"
        onConfirm={doVoid}
        onCancel={() => setConfirmVoid(null)}
      />
    </div>
  );
}
